<template>
  <HeaderComponent />
  
  <HeroComponent 
    image="https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
    text="Privacy Policy" 
    subtitle="Your privacy is important to us. Learn how we collect, use, and protect your information"
  />

  <!-- Privacy Content -->
  <div class="py-20 bg-gray-50">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        
        <!-- Last Updated -->
        <div class="bg-white rounded-2xl shadow-lg p-8 mb-8" data-sal="slide-up" data-sal-delay="200">
          <p class="text-gray-600 text-center">
            <strong>Last Updated:</strong> {{ lastUpdated }}
          </p>
        </div>

        <!-- Privacy Sections -->
        <div class="bg-white rounded-3xl shadow-lg p-8 md:p-12" data-sal="slide-up" data-sal-delay="400">
          <div class="prose prose-lg max-w-none">
            
            <!-- Introduction -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">1. Data Controller and Contact Information</h2>
              <div class="bg-blue-50 rounded-lg p-6 mb-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Data Controller:</h3>
                <p class="text-gray-600 leading-relaxed mb-2">
                  <strong>DIGINOVA</strong><br>
                  Switzerland<br>
                  Email: contact@diginova.ch<br>
                  Data Protection Officer: contact@diginova.ch
                </p>
              </div>
              <p class="text-gray-600 leading-relaxed mb-4">
                DIGINOVA ("we," "our," or "us") is committed to protecting your privacy in accordance with the EU General Data Protection Regulation (GDPR) and the Swiss Federal Act on Data Protection (nLPD). This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data.
              </p>
              <p class="text-gray-600 leading-relaxed">
                <strong>Important:</strong> By using our services, you acknowledge that you have read and understood this policy. For data processing that requires consent, we will obtain your explicit consent before processing.
              </p>
            </section>

            <!-- Information We Collect -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">2. Information We Collect</h2>
              
              <div class="space-y-6">
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">2.1 Personal Information</h3>
                  <p class="text-gray-600 leading-relaxed mb-2">
                    We may collect personal information that you voluntarily provide, including:
                  </p>
                  <ul class="text-gray-600 leading-relaxed pl-6">
                    <li class="mb-2">• Name and contact information (email, phone number)</li>
                    <li class="mb-2">• Company information</li>
                    <li class="mb-2">• Project requirements and preferences</li>
                    <li class="mb-2">• Payment information (processed securely through third parties)</li>
                  </ul>
                </div>

                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">2.2 Automatically Collected Information</h3>
                  <p class="text-gray-600 leading-relaxed mb-2">
                    When you visit our website, we may automatically collect:
                  </p>
                  <ul class="text-gray-600 leading-relaxed pl-6">
                    <li class="mb-2">• IP address and location data</li>
                    <li class="mb-2">• Browser type and version</li>
                    <li class="mb-2">• Pages visited and time spent on site</li>
                    <li class="mb-2">• Referring website</li>
                    <li class="mb-2">• Device information</li>
                  </ul>
                </div>

                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">2.3 Cookies and Tracking Technologies</h3>
                  <p class="text-gray-600 leading-relaxed mb-4">
                    We use cookies and similar technologies as detailed below. You can manage your cookie preferences through our cookie banner or browser settings.
                  </p>
                  
                  <div class="bg-gray-50 rounded-lg p-4">
                    <h4 class="font-semibold text-gray-900 mb-2">Cookie Categories:</h4>
                    <ul class="text-sm text-gray-600 space-y-1">
                      <li><strong>Essential:</strong> Required for website functionality (no consent needed)</li>
                      <li><strong>Analytics:</strong> Website usage statistics (legitimate interest/consent)</li>
                      <li><strong>Marketing:</strong> Personalized advertising (consent required)</li>
                      <li><strong>Preferences:</strong> Remember your settings (legitimate interest)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <!-- Legal Basis and Purposes -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">3. Legal Basis and Purposes of Processing</h2>
              <p class="text-gray-600 leading-relaxed mb-6">
                We process your personal data based on the following legal grounds under GDPR Article 6 and nLPD:
              </p>
              
              <div class="space-y-6">
                <div class="border-l-4 border-primary pl-4">
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">3.1 Contract Performance (Art. 6(1)(b) GDPR)</h3>
                  <p class="text-gray-600 leading-relaxed mb-2">Processing necessary for contract execution:</p>
                  <ul class="text-gray-600 leading-relaxed pl-6">
                    <li class="mb-1">• Providing our design and development services</li>
                    <li class="mb-1">• Project communication and delivery</li>
                    <li class="mb-1">• Payment processing and invoicing</li>
                    <li class="mb-1">• Customer support and account management</li>
                  </ul>
                </div>
                
                <div class="border-l-4 border-green-500 pl-4">
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">3.2 Legitimate Interest (Art. 6(1)(f) GDPR)</h3>
                  <p class="text-gray-600 leading-relaxed mb-2">Processing based on our legitimate business interests:</p>
                  <ul class="text-gray-600 leading-relaxed pl-6">
                    <li class="mb-1">• Website analytics and improvement</li>
                    <li class="mb-1">• Security monitoring and fraud prevention</li>
                    <li class="mb-1">• Business development and portfolio display</li>
                    <li class="mb-1">• Responding to inquiries and providing information</li>
                  </ul>
                </div>
                
                <div class="border-l-4 border-yellow-500 pl-4">
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">3.3 Consent (Art. 6(1)(a) GDPR)</h3>
                  <p class="text-gray-600 leading-relaxed mb-2">Processing based on your explicit consent:</p>
                  <ul class="text-gray-600 leading-relaxed pl-6">
                    <li class="mb-1">• Marketing communications and newsletters</li>
                    <li class="mb-1">• Non-essential cookies and tracking</li>
                    <li class="mb-1">• Testimonials and case study participation</li>
                  </ul>
                </div>
                
                <div class="border-l-4 border-red-500 pl-4">
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">3.4 Legal Obligation (Art. 6(1)(c) GDPR)</h3>
                  <p class="text-gray-600 leading-relaxed mb-2">Processing required by law:</p>
                  <ul class="text-gray-600 leading-relaxed pl-6">
                    <li class="mb-1">• Tax and accounting obligations</li>
                    <li class="mb-1">• Regulatory compliance</li>
                    <li class="mb-1">• Legal proceedings and law enforcement requests</li>
                  </ul>
                </div>
              </div>
            </section>

            <!-- Information Sharing -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">4. Information Sharing and Disclosure</h2>
              <div class="space-y-4">
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">4.1 General Policy</h3>
                  <p class="text-gray-600 leading-relaxed">
                    We do not sell, trade, or rent your personal information to third parties without your explicit consent.
                  </p>
                </div>

                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">4.2 Service Providers</h3>
                  <p class="text-gray-600 leading-relaxed">
                    We may share information with trusted third-party service providers who assist in operating our business, such as:
                  </p>
                  <ul class="text-gray-600 leading-relaxed pl-6 mt-2">
                    <li class="mb-2">• Payment processors</li>
                    <li class="mb-2">• Email service providers</li>
                    <li class="mb-2">• Analytics services</li>
                    <li class="mb-2">• Cloud storage providers</li>
                  </ul>
                </div>

                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">4.3 Legal Requirements</h3>
                  <p class="text-gray-600 leading-relaxed">
                    We may disclose information when required by law, court order, or to protect our rights and safety.
                  </p>
                </div>
              </div>
            </section>

            <!-- Data Security -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">5. Data Security</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                We implement appropriate security measures to protect your information, including:
              </p>
              <ul class="text-gray-600 leading-relaxed pl-6">
                <li class="mb-2">• SSL encryption for data transmission</li>
                <li class="mb-2">• Secure servers and databases</li>
                <li class="mb-2">• Regular security updates and monitoring</li>
                <li class="mb-2">• Limited access to personal information</li>
                <li class="mb-2">• Employee training on data protection</li>
              </ul>
              <p class="text-gray-600 leading-relaxed mt-4">
                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your information.
              </p>
            </section>

            <!-- Data Retention -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">6. Data Retention Periods</h2>
              <p class="text-gray-600 leading-relaxed mb-6">
                We retain personal data only as long as necessary for the purposes outlined in this policy, in compliance with GDPR and nLPD requirements:
              </p>
              
              <div class="overflow-x-auto">
                <table class="w-full bg-white border border-[#d1d5db] rounded-lg">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Category</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retention Period</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Legal Basis</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Client project files</td>
                      <td class="px-6 py-4 text-sm text-gray-600">3 years after project completion</td>
                      <td class="px-6 py-4 text-sm text-gray-600">Contract, legal obligation</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Financial records</td>
                      <td class="px-6 py-4 text-sm text-gray-600">10 years (tax requirements)</td>
                      <td class="px-6 py-4 text-sm text-gray-600">Legal obligation</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Marketing communications</td>
                      <td class="px-6 py-4 text-sm text-gray-600">Until consent withdrawn</td>
                      <td class="px-6 py-4 text-sm text-gray-600">Consent</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Website analytics</td>
                      <td class="px-6 py-4 text-sm text-gray-600">26 months maximum</td>
                      <td class="px-6 py-4 text-sm text-gray-600">Legitimate interest</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Inquiry/Contact forms</td>
                      <td class="px-6 py-4 text-sm text-gray-600">2 years if no contract</td>
                      <td class="px-6 py-4 text-sm text-gray-600">Legitimate interest</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p class="text-gray-600 leading-relaxed mt-4 text-sm">
                Data will be securely deleted or anonymized when the retention period expires, unless legal obligations require longer retention.
              </p>
            </section>

            <!-- Your Rights -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">7. Your Data Protection Rights (GDPR & nLPD)</h2>
              <p class="text-gray-600 leading-relaxed mb-6">
                Under GDPR and nLPD, you have the following rights regarding your personal data:
              </p>
              
              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right of Access (Art. 15 GDPR)</h3>
                  <p class="text-gray-600 text-sm">Request information about your personal data we process and receive a copy of your data.</p>
                </div>
                
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right to Rectification (Art. 16 GDPR)</h3>
                  <p class="text-gray-600 text-sm">Request correction of inaccurate or incomplete personal data.</p>
                </div>
                
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right to Erasure (Art. 17 GDPR)</h3>
                  <p class="text-gray-600 text-sm">Request deletion of your personal data under certain circumstances.</p>
                </div>
                
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right to Restrict Processing (Art. 18 GDPR)</h3>
                  <p class="text-gray-600 text-sm">Request limitation of processing in specific situations.</p>
                </div>
                
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right to Data Portability (Art. 20 GDPR)</h3>
                  <p class="text-gray-600 text-sm">Receive your data in a structured, machine-readable format.</p>
                </div>
                
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right to Object (Art. 21 GDPR)</h3>
                  <p class="text-gray-600 text-sm">Object to processing based on legitimate interests or direct marketing.</p>
                </div>
                
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right to Withdraw Consent</h3>
                  <p class="text-gray-600 text-sm">Withdraw consent at any time for consent-based processing.</p>
                </div>
                
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">✓ Right to Lodge a Complaint</h3>
                  <p class="text-gray-600 text-sm">File a complaint with the Swiss FDPIC or your local data protection authority.</p>
                </div>
              </div>
              
              <div class="bg-blue-50 rounded-lg p-6 mt-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">How to Exercise Your Rights:</h3>
                <p class="text-gray-600 leading-relaxed mb-2">
                  Contact us at <strong>contact@diginova.ch</strong> with "Data Protection Request" in the subject line.
                </p>
                <p class="text-gray-600 leading-relaxed text-sm">
                  We will respond within 30 days (GDPR) or 30 days (nLPD). Identity verification may be required.
                </p>
              </div>
              
              <div class="bg-yellow-50 rounded-lg p-6 mt-4">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Supervisory Authorities:</h3>
                <p class="text-gray-600 leading-relaxed text-sm">
                  <strong>Switzerland:</strong> Federal Data Protection and Information Commissioner (FDPIC)<br>
                  <strong>EU:</strong> Your local data protection authority or the lead supervisory authority
                </p>
              </div>
            </section>

            <!-- Third-Party Links -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">8. Third-Party Links</h2>
              <p class="text-gray-600 leading-relaxed">
                Our website may contain links to third-party sites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <!-- International Transfers -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">9. International Data Transfers</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                Your personal data may be transferred to and processed in countries outside Switzerland/EEA. We ensure appropriate safeguards through:
              </p>
              <ul class="text-gray-600 leading-relaxed pl-6 mb-4">
                <li class="mb-2">• Adequacy decisions by the European Commission or Swiss FDPIC</li>
                <li class="mb-2">• Standard Contractual Clauses (SCCs) approved by the EU Commission</li>
                <li class="mb-2">• Binding Corporate Rules for multinational service providers</li>
                <li class="mb-2">• Certification schemes and codes of conduct</li>
              </ul>
              <p class="text-gray-600 leading-relaxed text-sm">
                You can request information about the specific safeguards for transfers to third countries by contacting us.
              </p>
            </section>

            <!-- Data Breach Notification -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">10. Data Breach Notification</h2>
              <div class="bg-red-50 rounded-lg p-6">
                <p class="text-gray-600 leading-relaxed mb-4">
                  In the unlikely event of a personal data breach that poses a risk to your rights and freedoms, we will:
                </p>
                <ul class="text-gray-600 leading-relaxed pl-6">
                  <li class="mb-2">• Notify the relevant supervisory authority within 72 hours (GDPR Art. 33)</li>
                  <li class="mb-2">• Inform affected individuals without undue delay if high risk exists</li>
                  <li class="mb-2">• Provide clear information about the nature of the breach</li>
                  <li class="mb-2">• Outline measures taken to address the breach and prevent recurrence</li>
                  <li class="mb-2">• Offer recommendations to protect affected individuals</li>
                </ul>
              </div>
            </section>

            <!-- Children's Privacy -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">11. Children's Privacy (Under 16)</h2>
              <div class="bg-yellow-50 rounded-lg p-6">
                <p class="text-gray-600 leading-relaxed mb-4">
                  Our services are not directed to individuals under 16 years of age (GDPR minimum age for digital consent). We do not knowingly collect personal data from children under 16 without verifiable parental consent.
                </p>
                <p class="text-gray-600 leading-relaxed">
                  <strong>If you are a parent/guardian:</strong> If you become aware that your child has provided us with personal data, please contact us immediately at contact@diginova.ch. We will delete such information from our records within 30 days.
                </p>
              </div>
            </section>

            <!-- Changes to Policy -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">12. Changes to This Policy</h2>
              <div class="bg-blue-50 rounded-lg p-6">
                <p class="text-gray-600 leading-relaxed mb-4">
                  We may update this Privacy Policy to reflect changes in our practices, technology, legal requirements, or other factors.
                </p>
                <p class="text-gray-600 leading-relaxed mb-4">
                  <strong>How we notify you:</strong>
                </p>
                <ul class="text-gray-600 leading-relaxed pl-6 mb-4">
                  <li class="mb-2">• Email notification for material changes (active clients)</li>
                  <li class="mb-2">• Prominent website notice for 30 days</li>
                  <li class="mb-2">• Updated "Last Updated" date</li>
                </ul>
                <p class="text-gray-600 leading-relaxed text-sm">
                  Continued use after notification period constitutes acceptance. If you disagree with changes, please stop using our services and contact us to exercise your data rights.
                </p>
              </div>
            </section>

            <!-- Contact -->
            <section class="mb-8">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">13. Contact Information & Complaints</h2>
              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4">Data Controller Contact:</h3>
                  <p class="text-gray-600 mb-2"><strong>DIGINOVA</strong></p>
                  <p class="text-gray-600 mb-2"><strong>Email:</strong> contact@diginova.ch</p>
                  <p class="text-gray-600 mb-2"><strong>Data Protection Officer:</strong> contact@diginova.ch</p>
                  <p class="text-gray-600"><strong>Address:</strong> Switzerland</p>
                </div>
                
                <div class="bg-blue-50 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4">Supervisory Authorities:</h3>
                  <p class="text-gray-600 mb-2"><strong>Switzerland:</strong></p>
                  <p class="text-gray-600 text-sm mb-3">Federal Data Protection and Information Commissioner (FDPIC)<br>
www.edoeb.admin.ch</p>
                  <p class="text-gray-600 mb-2"><strong>EU/EEA:</strong></p>
                  <p class="text-gray-600 text-sm">Your local data protection authority<br>
Find yours at: ec.europa.eu/justice/data-protection</p>
                </div>
              </div>
              
              <div class="bg-green-50 rounded-lg p-6 mt-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Response Timeframes:</h3>
                <p class="text-gray-600 text-sm">
                  • General inquiries: 5 business days<br>
                  • Data protection requests: 30 days (may be extended by 60 days for complex requests)<br>
                  • Urgent security matters: 24-48 hours
                </p>
              </div>
            </section>
          </div>
        </div>

        <!-- Contact CTA -->
        <div class="text-center mt-12 bg-white rounded-3xl p-8 shadow-lg" data-sal="slide-up" data-sal-delay="600">
          <h3 class="text-2xl font-bold text-gray-900 mb-4">Privacy Questions?</h3>
          <p class="text-gray-600 mb-6">
            We're committed to transparency about how we handle your data. Contact us with any privacy-related questions.
          </p>
          <router-link 
            to="/contact" 
            class="inline-block bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Contact Us
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <FooterComponent />
</template>

<script>
import HeaderComponent from '@/components/Header.vue'
import FooterComponent from '@/components/Footer.vue'
import HeroComponent from '@/components/Hero.vue'

export default {
  name: 'PrivacyView',
  components: {
    HeaderComponent,
    FooterComponent,
    HeroComponent
  },
  data() {
    return {
      lastUpdated: 'January 1, 2025'
    };
  },
  mounted() {
    if (typeof sal !== 'undefined') {
      sal({
        duration: 500,
        threshold: 0.1,
        once: true
      });
    }
  }
}
</script>

<style scoped>
.prose h2 {
  border-bottom: 1px solid #d1d5db; 
  padding-bottom: 0.5rem;
}

.prose h3 {
  color: var(--color-primary);
}

.prose ul {
  list-style: none;
}

.prose strong {
  color: var(--color-gray-900);
}
</style>